import '../scss/app.scss';
import {createApp, h} from 'vue';


// Import NSW Design System module
import {initNSWDesignSystem, reinitNSWComponents} from './nsw-design-system.js';

// Import Cookie Consent module
import {initCookieConsent} from './cookie-consent.js';

// ---------------------------------------------------------------------------
// Vue Component Mounting
//
// Each Vue component is mounted individually with a programmatic render()
// function. This uses the runtime-only Vue build (no template compiler),
// which eliminates unsafe-eval CSP violations and reduces bundle size ~60KB.
//
// Components are lazy-loaded — webpack splits each into a separate chunk
// that only downloads when its custom element tag appears in the DOM.
// ---------------------------------------------------------------------------

/**
 * Component registry: maps custom element tags to their async loaders.
 * SignaturePad is omitted — it's a child component of HCPReferralForm,
 * imported directly within that SFC.
 */
const componentRegistry = {
    'get-started-form':       () => import('./components/GetStartedForm.vue'),
    'hcp-referral-form':      () => import('./components/HCPReferralForm.vue'),
    'bmi-calculator':         () => import('./components/BMICalculator.vue'),
    'pregnancy-weight-tracker': () => import('./components/pregnancy-wgc/PregnancyWeightTracker.vue'),
    'testimonials-wrapper':   () => import('./components/TestimonialsWrapper.vue'),
};

/**
 * Parse a v-bind (:attr) attribute value into a JavaScript value.
 *
 * Handles three patterns found in our SilverStripe templates:
 *   1. JSON objects/arrays:  :validation-schema='{"minAge":18}'  → parsed object
 *   2. Quoted string literals: :save-url="'/api/save'"           → '/api/save'
 *   3. Global variable refs:  :initial-data="pregnancyTrackerData" → window.pregnancyTrackerData
 */
function parseBindValue(value) {
    // 1. Try JSON (handles objects, arrays, numbers, double-quoted strings)
    try {
        return JSON.parse(value);
    } catch {
        // 2. Single-quoted string literal from SilverStripe template output
        if (value.startsWith("'") && value.endsWith("'")) {
            return value.slice(1, -1);
        }
        // 3. Global variable reference (e.g. window.pregnancyTrackerData)
        if (typeof window[value] !== 'undefined') {
            return window[value];
        }
        return value;
    }
}

/**
 * Extract props from a custom element's DOM attributes.
 *
 *   :attr="value"  → parsed via parseBindValue (v-bind equivalent)
 *    attr="value"  → plain string
 *
 * Kebab-case attribute names are converted to camelCase for Vue props.
 */
function extractProps(el) {
    const props = {};
    for (const attr of el.attributes) {
        let name = attr.name;
        let value = attr.value;

        // Handle Vue v-bind shorthand (:prop)
        if (name.startsWith(':')) {
            name = name.slice(1);
            value = parseBindValue(value);
        }

        // kebab-case → camelCase
        const camelName = name.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
        props[camelName] = value;
    }
    return props;
}

/**
 * Mount all Vue components found in the DOM.
 *
 * For each registered custom element tag, finds matching DOM elements,
 * extracts their attributes as props, lazy-loads the SFC, and mounts
 * a dedicated Vue app with a programmatic render function.
 */
function mountVueComponents() {
    for (const [tag, loader] of Object.entries(componentRegistry)) {
        document.querySelectorAll(tag).forEach(async (el) => {
            const props = extractProps(el);
            const module = await loader();
            const Component = module.default;

            const app = createApp({
                render() {
                    return h(Component, props);
                },
                mounted() {
                    // Reinitialize NSWDS so popovers etc. bind to the new DOM
                    this.$nextTick(() => reinitNSWComponents());
                },
                updated() {
                    // Async child renders may add new NSWDS elements
                    reinitNSWComponents();
                },
            });

            app.mount(el);
        });
    }
}

// Application Initialization
function bootstrap() {
    console.log('GetHealthy theme loaded successfully!');

    // Initialize NSW Design System
    initNSWDesignSystem();

    // Mount Vue components (was previously called at module top level; under
    // `type=module` loading that ran before some target elements were attached
    // by other deferred scripts, leading to setRef null errors).
    mountVueComponents();

    // Initialize Cookie Consent if configuration is available
    if (window.nswCookieConsentConfig) {
        initCookieConsent(window.nswCookieConsentConfig);
    }

    // Custom application logic can go here
    initCustomComponents();

    console.log('Application initialization complete');
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
} else {
    bootstrap();
}

/**
 * Initialize custom application components
 */
function initCustomComponents() {
    // Add any custom theme-specific JavaScript here

    // Example: Custom scroll behavior
    initSmoothScrolling();

    // Example: Custom analytics or tracking
    initAnalytics();

    // Language dropdown toggle
    initLanguageDropdown();

    console.log('Custom components initialized');
}

/**
 * Initialize smooth scrolling for anchor links
 * Also handles focus management for skip links (accessibility)
 */
function initSmoothScrolling() {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');

    anchorLinks.forEach(link => {
        link.addEventListener('click', function (e) {
            const targetId = this.getAttribute('href');

            // Skip if href is just "#" or empty
            if (!targetId || targetId === '#') {
                return;
            }

            const targetElement = document.querySelector(targetId);

            if (targetElement) {
                e.preventDefault();

                // Scroll to target
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                // Make target focusable if it isn't already
                if (!targetElement.hasAttribute('tabindex')) {
                    targetElement.setAttribute('tabindex', '-1');
                }

                // Move focus to target element (critical for skip links)
                // Small delay to ensure scroll has started
                setTimeout(() => {
                    targetElement.focus();

                    // Remove tabindex after focus (unless it was already there)
                    // This prevents the element from staying in tab order
                    targetElement.addEventListener('blur', function removeFocusHandler() {
                        if (targetElement.getAttribute('tabindex') === '-1') {
                            targetElement.removeAttribute('tabindex');
                        }
                        targetElement.removeEventListener('blur', removeFocusHandler);
                    });
                }, 100);
            }
        });
    });
}

/**
 * Initialize analytics or tracking
 */
function initAnalytics() {
    // Placeholder for analytics initialization
    // This would typically include Google Analytics, Adobe Analytics, etc.
    console.log('Analytics initialized');
}

/**
 * Initialize language dropdown toggle behavior
 */
function initLanguageDropdown() {
    const languageButton = document.querySelector('.js-open-language');
    const languageMenu = document.getElementById('language-menu');

    if (!languageButton || !languageMenu) {
        return; // Exit if elements don't exist
    }

    // Toggle dropdown on button click
    languageButton.addEventListener('click', function(e) {
        e.stopPropagation();
        const isExpanded = languageButton.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
            closeLanguageDropdown();
        } else {
            openLanguageDropdown();
        }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function(e) {
        if (!languageButton.contains(e.target) && !languageMenu.contains(e.target)) {
            closeLanguageDropdown();
        }
    });

    // Close dropdown on Escape key
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && languageButton.getAttribute('aria-expanded') === 'true') {
            closeLanguageDropdown();
            languageButton.focus();
        }
    });

    // Keyboard navigation within menu
    languageMenu.addEventListener('keydown', function(e) {
        const menuItems = Array.from(languageMenu.querySelectorAll('a[role="menuitem"]'));
        const currentIndex = menuItems.indexOf(document.activeElement);

        switch(e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (currentIndex < menuItems.length - 1) {
                    menuItems[currentIndex + 1].focus();
                } else {
                    menuItems[0].focus();
                }
                break;

            case 'ArrowUp':
                e.preventDefault();
                if (currentIndex > 0) {
                    menuItems[currentIndex - 1].focus();
                } else {
                    menuItems[menuItems.length - 1].focus();
                }
                break;

            case 'Home':
                e.preventDefault();
                menuItems[0].focus();
                break;

            case 'End':
                e.preventDefault();
                menuItems[menuItems.length - 1].focus();
                break;

            case 'Tab':
                // Close on tab out
                closeLanguageDropdown();
                break;
        }
    });

    function openLanguageDropdown() {
        languageButton.setAttribute('aria-expanded', 'true');
        languageMenu.setAttribute('aria-hidden', 'false');
        languageMenu.classList.remove('hidden');

        // Focus first menu item
        const firstItem = languageMenu.querySelector('a[role="menuitem"]');
        if (firstItem) {
            setTimeout(() => firstItem.focus(), 50);
        }
    }

    function closeLanguageDropdown() {
        languageButton.setAttribute('aria-expanded', 'false');
        languageMenu.setAttribute('aria-hidden', 'true');
        languageMenu.classList.add('hidden');
    }

    console.log('Language dropdown initialized');
}
